import React, { useState, useEffect } from 'react';
import './MenuPage.css';

import RanksIcon from '../assets/icons/yugiohMeta.svg';
import ShopIcon from '../assets/icons/ShopIcon.png';
import StarIcon from '../assets/icons/StarIcon.png';
import ToolIcon from '../assets/icons/ToolIcon.png';
import ProfileIcon from '../assets/icons/ProfileIcon.png';
import SettingsIcon from '../assets/icons/SettingsIcon.svg';
import DuelistIcon from '../assets/icons/DuelistIcon.png';

import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { getLoggedInUser } from '../logic/loginLogic';
import { playSound } from '../utils/soundPlayer';
import { useTranslation } from 'react-i18next';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';

const MenuPage = () => {
  const { t } = useTranslation();
  const { unreadCount } = useUnreadMessages();

  const [dp, setDp] = useState(null);
  const [pseudo, setPseudo] = useState('');
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();

  const [hasOpenTournament, setHasOpenTournament] = useState(false);

  useEffect(() => {
    const fetchOpenTournaments = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'tournament', 'upcoming', 'list'));
        const tournaments = snapshot.docs.map(doc => doc.data());
        const hasAnyOpen = tournaments.some(t => t.started === false);
        setHasOpenTournament(hasAnyOpen);
      } catch (error) {
        console.error('Error checking open tournaments:', error);
      }
    };

    fetchOpenTournaments();
  }, []);

  useEffect(() => {
    const user = getLoggedInUser();
    if (!user) {
      navigate('/');
    } else {
      setUserId(user.uid);
    }
  }, [navigate]);

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(doc(db, 'users', userId), (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setDp(userData.dp || 0);
        setPseudo(userData.pseudo || 'Duelist');
      } else {
        console.log('User not found in Firestore');
      }
    });

    return () => unsubscribe();
  }, [userId]);

  const handleNavClick = (path) => {
    playSound('menu');
    navigate(path);
  };

  return (
    <div className="menu-container">
      {/* Animated Background Elements */}
      <div className="background-orbs">
        <div className="orb orb-slifer"></div>

      </div>

      {/* Background Logo */}
      <img
        onMouseDown={(e) => e.button === 2 && e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
        draggable={false}
        src={require('../assets/icons/yme.png')}
        alt="Background Logo"
        className="background-logo"
      />

      {/* Header Section */}
      <div className="menu-header">
        <div className="menu-title">{t('menu.title')}</div>
      </div>

      {/* Menu Buttons Grid */}
      <div className="menu-grid">
        <div className="menu-card slifer-card" onClick={() => handleNavClick('/shop')}>
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="icon-container">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={ShopIcon}
                alt="Shop"
                className="menu-icon"
              />
            </div>
            <div className="card-label">{t('menu.shop')}</div>
            <div className="card-description">{t('menu.shopDescription')}</div>
          </div>
        </div>

        <div
          className={`menu-card obelisk-card ${hasOpenTournament ? 'card-pulse' : ''}`}
          onClick={() => handleNavClick('/tournament')}
        >
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="icon-container">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={StarIcon}
                alt="Tournament"
                className="menu-icon"
              />
              {unreadCount > 0 && (
                <div className="menu-notification-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="currentColor"/>
                    <circle cx="8" cy="10" r="1" fill="currentColor"/>
                    <circle cx="12" cy="10" r="1" fill="currentColor"/>
                    <circle cx="16" cy="10" r="1" fill="currentColor"/>
                  </svg>
                  <span className="badge-count">{unreadCount}</span>
                </div>
              )}
            </div>
            <div className="card-label">{t('menu.tournament')}</div>
            <div className="card-description">{t('menu.tournamentDescription')}</div>
          </div>
        </div>

        <div className="menu-card slifer-card" onClick={() => handleNavClick('/tool')}>
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="icon-container">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={ToolIcon}
                alt="Tool"
                className="menu-icon"
              />
            </div>
            <div className="card-label">{t('menu.tool')}</div>
            <div className="card-description">{t('menu.toolDescription')}</div>
          </div>
        </div>

        <div className="menu-card slifer-card" onClick={() => handleNavClick('/duelists')}>
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="icon-container">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={DuelistIcon}
                alt="Duelists"
                className="menu-icon"
              />
            </div>
            <div className="card-label">{t('menu.duelists')}</div>
            <div className="card-description">{t('menu.duelistsDescription')}</div>
          </div>
        </div>

        <div className="menu-card slifer-card" onClick={() => handleNavClick('/profile')}>
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="icon-container">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={ProfileIcon}
                alt="Profile"
                className="menu-icon"
              />
            </div>
            <div className="card-label">{t('menu.profile')}</div>
            <div className="card-description">{t('menu.profileDescription')}</div>
          </div>
        </div>

        <div className="menu-card obelisk-card" onClick={() => handleNavClick('/account-settings')}>
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="icon-container">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={SettingsIcon}
                alt="Account Settings"
                className="menu-icon"
              />
            </div>
            <div className="card-label">{t('menu.accountSettings')}</div>
            <div className="card-description">{t('menu.accountSettingsDescription')}</div>
          </div>
        </div>
      </div>

      {/* User Stats */}
      <div className="user-info">
        ID: {pseudo || '...'}&nbsp;&nbsp;&nbsp;  {t('menu.dp')}: {dp !== null ? dp : '...'}
      </div>
    </div>
  );
};

export default MenuPage;